import React, { useMemo } from "react";
import DayPicker from "./DayPicker";
import ClassNames from "classnames";
import { capitalize } from "lodash-es";
import Tooltip from "../../Tooltip/Tooltip";

const allDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

type WeekdaySelectionSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

interface WeekdaySelectionProps {
  firstDay?: string;
  className?: string;
  "data-testid"?: string;
  allowedDays?: string[];
  onChange: (selectedDays: string[]) => void;
  selectedDays?: string[];
  multi?: boolean;
  label?: React.ReactNode;
  hideLabelHelper?: boolean;
  labelTop?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: WeekdaySelectionSize;
  labelWidth?: number | string | false;
  isInline?: boolean;
  isLarge?: boolean;
  noMargin?: boolean;
  error?: boolean;
  withHelp?: string;
  suffixIconButton?: React.ReactNode;
}

const WeekdaySelection: React.FC<WeekdaySelectionProps> = ({
  firstDay = "monday",
  className,
  "data-testid": dataTestId,
  allowedDays = allDays,
  onChange,
  selectedDays = [],
  multi = false,
  label = "",
  hideLabelHelper = false,
  labelTop = false,
  required = true,
  disabled = false,
  readOnly = false,
  leftIcon = null,
  rightIcon = null,
  size = "m",
  labelWidth = false,
  isInline = false,
  isLarge = false,
  noMargin = false,
  error,
  withHelp = "",
  suffixIconButton = null,
}) => {
  const firstDayIndex = useMemo(
    () => allDays.findIndex((day) => day === firstDay),
    [firstDay]
  );
  const sortedWeek = useMemo(() => {
    const sorted: string[] = [];

    for (let i = firstDayIndex, counter = 0; counter < 7; i++, counter++) {
      sorted.push(allDays[i % 7]);
    }
    return sorted;
  }, [firstDayIndex]);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-weekday-selection",
    // wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
      "has-suffix-icon-btn": suffixIconButton,
    },
    className
  );
  const onDayClick = (weekday: string) => {
    if (multi) {
      if (selectedDays.includes(weekday)) {
        onChange(selectedDays.filter((day) => day !== weekday));
      } else {
        onChange([...selectedDays, weekday]);
      }
    } else {
      onChange([weekday]);
    }
  };

  return (
    <div
      className={`weekday-selection ${classNameWrapper}`}
      data-testid="weekday-selection"
    >
      {!!label && (
        <label
          className={`input-label`}
          style={{ width: labelWidth ? labelWidth : "" }}
          data-testid={`${dataTestId}-label`}
        >
          {label}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
          {withHelp && (
            <>
              <Tooltip type="info" content={withHelp}>
                <i className={`seem-icon seem-icon-help`} />
              </Tooltip>
            </>
          )}
        </label>
      )}
      {sortedWeek
        .filter((day) => allowedDays.includes(day))
        .map((weekday, index) => (
          <DayPicker
            key={index}
            label={weekday?.[0]?.toUpperCase()}
            title={capitalize(weekday)}
            selected={selectedDays.includes(weekday)}
            onClick={() => onDayClick(weekday)}
          />
        ))}
    </div>
  );
};

export default WeekdaySelection;
